import React, {
  useState, useEffect, useRef, useContext, useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  collection, query, orderBy, limitToLast, onSnapshot,
  addDoc, updateDoc, doc, getDoc, serverTimestamp, increment,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AuthContext } from '../../context/AuthContext';
import { apiClient } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { ICONS } from '../../constants/imageConstants';
import '../../styles/chatNotification.css';

const MAX_STACKED   = 3;
const DISMISS_MS    = 6000;

function makeRoomId(a, b) {
  return [String(a), String(b)].sort().join('_');
}

/**
 * MessageNotificationManager
 * – Dark floating bubble (Discord-style)
 * – Messages stack up to 3; newest slides in from bottom
 * – One card at a time; different sender replaces existing card
 * – Reply sends inline; tapping card body navigates to chat
 * – Accurate new-message counter that resets on dismiss/sender change
 */
export function MessageNotificationManager() {
  const { user, firebaseUser } = useContext(AuthContext);
  const currentUserUid = user?.id || firebaseUser?.uid;
  const isOnline = useSelector(state => state.ui.networkStatus.isOnline);
  const location = useLocation();
  const navigate = useNavigate();

  // notification: { senderUid, senderName, senderAvatar, msgs:[{id,text}], totalNew }
  const [notification, setNotification] = useState(null);
  const [dismissing, setDismissing]     = useState(false);
  const [replying, setReplying]         = useState(false);
  const [replyText, setReplyText]       = useState('');
  const [sending, setSending]           = useState(false);
  const [avatarErrors, setAvatarErrors] = useState({});

  const isMounted        = useRef(true);
  const mountedAt        = useRef(Date.now());
  const locationRef      = useRef(location.pathname);
  const notifiedIds      = useRef(new Set());
  const initializedRooms = useRef(new Set());
  const baselineUnread   = useRef({}); // roomId -> unread count
  const unsubscribers    = useRef([]);
  const dismissTimer     = useRef(null);
  // Tracks the current notification's sender so dismiss() can mark-as-read
  const notifSenderRef   = useRef(null);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    isMounted.current = true;
    mountedAt.current = Date.now();
    return () => {
      isMounted.current = false;
      clearTimeout(dismissTimer.current);
    };
  }, []);

  // ── Mark messages as read in Firestore ──────────────────────────────────
  const markAsRead = useCallback(async (senderUid) => {
    if (!currentUserUid || !senderUid) return;
    try {
      const rid = makeRoomId(currentUserUid, senderUid);
      baselineUnread.current[rid] = 0; // reset local baseline immediately
      await updateDoc(doc(db, 'chats', rid), {
        [`unreadCounts.${String(currentUserUid)}`]: 0,
      });
    } catch { /* non-critical */ }
  }, [currentUserUid]);

  // ── Dismiss (with exit animation only — does NOT mark as read) ─────────────
  const dismiss = useCallback(() => {
    clearTimeout(dismissTimer.current);
    setDismissing(true);
    setTimeout(() => {
      if (isMounted.current) {
        setNotification(null);
        setDismissing(false);
        setReplying(false);
        setReplyText('');
        setAvatarErrors({});
        notifSenderRef.current = null;
      }
    }, 280);
  }, []);

  useEffect(() => {
    // Auto-dismiss if we navigate to the chat page
    if (notification) {
      const onChatList = location.pathname === '/chat';
      const onThisChat = location.pathname === `/chat/${notification.senderUid}`;
      if (onChatList || onThisChat) {
        dismiss();
      }
    }
  }, [location.pathname, notification, dismiss]);

  const resetTimer = useCallback(() => {
    clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => {
      if (isMounted.current) dismiss();
    }, DISMISS_MS);
  }, [dismiss]);

  const pauseTimer = useCallback(() => clearTimeout(dismissTimer.current), []);

  // ── Navigate to chat on card click ───────────────────────────────────────
  const goToChat = useCallback((e) => {
    if (!notification) return;
    // Don't navigate when clicking buttons or the reply area
    if (e.target.closest('.chat-notif-actions') ||
        e.target.closest('.chat-notif-reply-area') ||
        e.target.closest('.chat-notif-close')) return;
    dismiss();
    navigate(`/chat/${notification.senderUid}`, {
      state: { name: notification.senderName, avatar: notification.senderAvatar },
    });
  }, [notification, dismiss, navigate]);

  // ── Inline reply send ────────────────────────────────────────────────────
  const sendReply = useCallback(async (e) => {
    e.stopPropagation();
    if (!replyText.trim() || !notification || sending) return;
    setSending(true);
    try {
      const rid = makeRoomId(currentUserUid, notification.senderUid);
      await addDoc(collection(db, 'chats', rid, 'messages'), {
        content:      replyText.trim(),
        sender_uid:   currentUserUid,
        receiver_uid: notification.senderUid,
        timestamp:    serverTimestamp(),
        edited:       false,
        isDeleted:    false,
      });
      await updateDoc(doc(db, 'chats', rid), {
        message:      replyText.trim(),
        last_message: replyText.trim(),
        last_time:    serverTimestamp(),
        [`unreadCounts.${notification.senderUid}`]: increment(1),
      });
      dismiss();
    } catch (err) {
      console.error('[MsgNotif] Reply failed:', err?.message);
    } finally {
      setSending(false);
    }
  }, [replyText, notification, sending, currentUserUid, dismiss]);

  // ── Incoming message dispatch ─────────────────────────────────────────────
  const dispatchMessage = useCallback((senderUid, senderName, senderAvatar, text, totalNew = 1) => {
    const newEntry = { id: `${Date.now()}-${Math.random()}`, text };
    notifSenderRef.current = senderUid;
    setNotification(prev => {
      if (prev && prev.senderUid === senderUid) {
        const msgs = [...prev.msgs, newEntry].slice(-MAX_STACKED);
        return { ...prev, msgs, totalNew }; 
      }
      setReplying(false);
      setReplyText('');
      return { senderUid, senderName, senderAvatar, msgs: [newEntry], totalNew };
    });
    resetTimer();
  }, [resetTimer]);

  // ── Stable route check — uses startsWith to handle any ID format ─────────
  const getActiveChatUid = useCallback(() => {
    const path = locationRef.current;
    if (!path.startsWith('/chat/')) return null;
    const uid = path.slice('/chat/'.length); // everything after /chat/
    return uid || null;
  }, []);

  // ── Per-room Firestore listener ───────────────────────────────────────────
  const setupRoomListener = useCallback((roomId, meta) => {
    // 1. Listen to the room document to keep baseline Unread perfectly in sync with the server
    const roomUnsub = onSnapshot(doc(db, 'chats', roomId), snap => {
      if (!isMounted.current) return;
      const data = snap.data() || {};
      const serverUnread = data?.unreadCounts?.[String(currentUserUid)] || 
                           data[`unreadCounts.${String(currentUserUid)}`] || 0;
      baselineUnread.current[roomId] = serverUnread;
    });

    // 2. Listen to the messages subcollection to trigger the actual notification popups
    const q = query(
      collection(db, 'chats', roomId, 'messages'),
      orderBy('timestamp', 'asc'),
      limitToLast(1),
    );
    
    const msgUnsub = onSnapshot(q, snap => {
      if (!isMounted.current) return;

      const isFirstSnapshot = !initializedRooms.current.has(roomId);
      if (isFirstSnapshot) {
        initializedRooms.current.add(roomId);
        // Mark all existing documents as notified so they don't trigger
        snap.docs.forEach(d => notifiedIds.current.add(d.id));
        return; // Skip processing historical messages
      }

      snap.docChanges().forEach(change => {
        if (change.type !== 'added') return;
        const msgId   = change.doc.id;
        const msgData = change.doc.data();

        if (notifiedIds.current.has(msgId)) return;
        if (String(msgData.sender_uid) === String(currentUserUid)) return;
        if (msgData.isDeleted) return;

        let ts = 0;
        if (msgData.timestamp?.toDate) ts = msgData.timestamp.toDate().getTime();
        else if (msgData.timestamp)    ts = new Date(msgData.timestamp).getTime();

        // Removed the flawed local clock vs server clock safety check here.
        // Since isFirstSnapshot skips historical docs, any 'added' change here 
        // is guaranteed to be a new incoming message!

        const parts      = roomId.split('_');
        const otherUid   = parts.find(p => String(p) !== String(currentUserUid));
        const currentPath = locationRef.current;

        // Suppress if:
        //  • user is on the conversations list (/chat)
        //  • user is already viewing THIS specific chat (/chat/:otherUid)
        const onChatList    = currentPath === '/chat';
        const onThisChat    = getActiveChatUid() === String(otherUid);
        if (onChatList || onThisChat) {
          notifiedIds.current.add(msgId);
          baselineUnread.current[roomId] = 0; // if they are looking at it, it's read
          return;
        }

        notifiedIds.current.add(msgId);

        // Increment baseline synchronously to avoid Firestore race conditions
        const currentUnread = (baselineUnread.current[roomId] || 0) + 1;
        baselineUnread.current[roomId] = currentUnread;
        
        dispatchMessage(
          String(otherUid),
          meta?.partnerName   || 'Team Member',
          meta?.partnerAvatar || null,
          msgData.content     || '📎 Attachment',
          currentUnread,
        );
      });
    }, err => {
      if (err?.code !== 'permission-denied')
        console.warn(`[MsgNotif] ${roomId}:`, err?.message);
    });

    // Return composite unsubscribe
    return () => {
      roomUnsub();
      msgUnsub();
    };
  }, [currentUserUid, getActiveChatUid, dispatchMessage]);

  // ── Room discovery ────────────────────────────────────────────────────────
  useEffect(() => {
    // Wait until both the PWA user and the Firebase session are fully ready
    if (!currentUserUid || !firebaseUser || !isOnline) return;
    let poll = null;

    const setup = async () => {
      try {
        const res   = await apiClient.get(API_ENDPOINTS.chats.rooms);
        const rooms = res.data?.data?.data || res.data?.data || res.data || [];
        if (!isMounted.current || !rooms.length) return;

        if (poll) { clearInterval(poll); poll = null; }

        const incoming = new Set(rooms.filter(r => r.room_id).map(r => r.room_id));
        const active   = new Set(unsubscribers.current.map(u => u.roomId));
        const changed  = incoming.size !== active.size || [...incoming].some(id => !active.has(id));
        if (!changed) return;

        unsubscribers.current.forEach(u => u.fn());
        unsubscribers.current = [];
        initializedRooms.current = new Set();

        unsubscribers.current = rooms.filter(r => r.room_id).map(room => {
          const rid     = room.room_id;
          const isSend  = String(currentUserUid) === String(room.sender_id);
          const partner = isSend ? room.receiver : room.sender;
          const name    = partner?.full_name ||
            `${partner?.first_name || ''} ${partner?.last_name || ''}`.trim() || 'Team Member';
          return { roomId: rid, fn: setupRoomListener(rid, { partnerName: name, partnerAvatar: partner?.profile_image || null }) };
        });
      } catch (err) {
        console.warn('[MsgNotif] fetch error:', err?.message);
      }
    };

    setup();
    poll = setInterval(setup, 30000);
    return () => {
      unsubscribers.current.forEach(u => u.fn());
      unsubscribers.current = [];
      clearInterval(poll);
    };
  }, [currentUserUid, firebaseUser, isOnline, setupRoomListener]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!notification) return null;

  const { senderUid, senderName, senderAvatar, msgs, totalNew } = notification;
  const fallback    = ICONS?.USER_AVATAR || '/avatar-placeholder.png';
  const avatarFailed = avatarErrors[senderUid];
  // First letter of sender name for letter-avatar fallback
  const initial = (senderName || '?')[0].toUpperCase();

  return (
    <div className="chat-notif-overlay">
      <div className={`chat-notif-card${dismissing ? ' chat-notif-card--dismissing' : ''}`} onClick={goToChat}>

        {/* Header */}
        <div className="chat-notif-header">
          <div className="chat-notif-avatar-wrap">
            {!avatarFailed && senderAvatar ? (
              <img
                className="chat-notif-avatar"
                src={senderAvatar}
                alt={senderName}
                onError={() => setAvatarErrors(prev => ({ ...prev, [senderUid]: true }))}
              />
            ) : (
              <div className="chat-notif-avatar chat-notif-avatar-letter">
                {initial}
              </div>
            )}
            <div className="chat-notif-icon-badge">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
              </svg>
            </div>
          </div>

          <div className="chat-notif-meta">
            <span className="chat-notif-sender-name">{senderName}</span>
            <span className="chat-notif-time">• now</span>
            {totalNew > 1 && (
              <span className="chat-notif-count">{totalNew} new</span>
            )}
          </div>

          <button
            className="chat-notif-close"
            onClick={e => { e.stopPropagation(); dismiss(); }}
            aria-label="Dismiss"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Stacked messages — newest slides in */}
        <div className="chat-notif-messages">
          {msgs.map((m, i) => (
            <p
              key={m.id}
              className={`chat-notif-msg${i === msgs.length - 1 ? ' chat-notif-msg-new' : ''}`}
            >
              {m.text}
            </p>
          ))}
        </div>

        {/* Reply input (shown when replying) */}
        {replying ? (
          <div className="chat-notif-reply-area" onClick={e => e.stopPropagation()}>
            <input
              autoFocus
              className="chat-notif-reply-input"
              placeholder="Type a reply…"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onFocus={pauseTimer}
              onBlur={resetTimer}
              onKeyDown={e => e.key === 'Enter' && sendReply(e)}
            />
            <button
              className="chat-notif-reply-send"
              disabled={!replyText.trim() || sending}
              onClick={sendReply}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
        ) : (
          <div className="chat-notif-actions">
            <button
              className="chat-notif-btn chat-notif-btn-reply"
              onClick={e => { e.stopPropagation(); setReplying(true); }}
            >
              Reply
            </button>
            <button
              className="chat-notif-btn chat-notif-btn-dismiss"
              onClick={e => {
                e.stopPropagation();
                markAsRead(notification.senderUid).then(() => dismiss());
              }}
            >
              Mark as Read
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
